import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, AlertTriangle, Loader2, CheckCircle2, XCircle, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function copy(t) {
  try { navigator.clipboard.writeText(t); toast.success('Gekopieerd'); }
  catch (_) { toast.error('Kopiëren mislukt'); }
}

export default function HomeyPhase3SafetyTab({ token }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const [devices, setDevices] = useState([]);
  const [consent, setConsent] = useState({});
  const [whitelist, setWhitelist] = useState({});
  const [emergency, setEmergency] = useState(false);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inspector, setInspector] = useState({
    device_key: 'smart_plug_with_energy',
    capability_id: 'onoff',
    value: 'true',
  });
  const [inspectResult, setInspectResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c, w, e, al] = await Promise.all([
        axios.get(`${API}/admin/homey-sim/devices`, H),
        axios.get(`${API}/admin/homey-sim/consent`, H),
        axios.get(`${API}/admin/homey-sim/whitelist`, H),
        axios.get(`${API}/admin/homey-sim/emergency-stop`, H),
        axios.get(`${API}/admin/homey-sim/audit-log`, { ...H, params: { limit: 50 } }),
      ]);
      setDevices(d.data?.devices || []);
      const cm = {}; (c.data?.items || []).forEach((i) => cm[i.device_key] = i.consented);
      setConsent(cm);
      const wm = {}; (w.data?.items || []).forEach((i) => wm[i.device_key] = i.whitelisted);
      setWhitelist(wm);
      setEmergency(e.data?.active || false);
      setAudit(al.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setConsentFor = async (device_key, consented) => {
    try {
      await axios.post(`${API}/admin/homey-sim/consent`, { device_key, consented }, H);
      setConsent({ ...consent, [device_key]: consented });
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const setWhitelistFor = async (device_key, wl) => {
    try {
      await axios.post(`${API}/admin/homey-sim/whitelist`, { device_key, whitelisted: wl }, H);
      setWhitelist({ ...whitelist, [device_key]: wl });
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const toggleEmergency = async () => {
    if (!emergency && !window.confirm('STOP ALL CONTROL — bevestig?')) return;
    try {
      const res = await axios.post(`${API}/admin/homey-sim/emergency-stop`,
        { active: !emergency }, H);
      setEmergency(res.data.emergency_stop_active);
      toast[res.data.emergency_stop_active ? 'error' : 'success'](
        res.data.emergency_stop_active ? 'EMERGENCY STOP ACTIVATED' : 'Emergency stop cleared');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const runInspector = async () => {
    try {
      let value = inspector.value;
      try { value = JSON.parse(inspector.value); } catch (_) {}
      const res = await axios.post(`${API}/admin/homey-sim/control/inspect`,
        { device_key: inspector.device_key,
          capability_id: inspector.capability_id, value }, H);
      setInspectResult(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const exportAudit = () => {
    const blob = new Blob([JSON.stringify(audit, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `audit-log-${Date.now()}.json`;
    a.click();
  };

  const blockedKeys = new Set(devices.filter((d) => d.control_status === 'control_blocked').map((d) => d.key));

  return (
    <div className="space-y-6" data-testid="homey-phase3-tab">
      {/* Emergency Stop */}
      <div className={`rounded-xl p-4 border-2 ${emergency ? 'bg-red-100 border-red-500' : 'bg-white border-border'}`}
           data-testid="emergency-block">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <AlertTriangle className={`w-8 h-8 ${emergency ? 'text-red-700 animate-pulse' : 'text-amber-600'}`} />
            <div>
              <div className="font-bold text-base">Emergency Stop</div>
              <div className="text-xs text-muted-foreground">
                {emergency ? 'ACTIEF — alle non-read-only acties geblokkeerd' : 'Inactief — sandbox/dry-run normaal'}
              </div>
            </div>
          </div>
          <button onClick={toggleEmergency}
            data-testid="emergency-toggle"
            className={`px-4 py-2 rounded-lg font-bold ${emergency ? 'bg-green-600 text-white' : 'bg-red-600 text-white hover:bg-red-700'}`}>
            {emergency ? 'CLEAR' : 'STOP ALL CONTROL'}
          </button>
        </div>
      </div>

      {/* Payload Inspector */}
      <div className="border border-border rounded-xl p-4 bg-white" data-testid="inspector-block">
        <div className="font-semibold mb-2">Payload Inspector</div>
        <div className="text-xs text-muted-foreground mb-3">
          Toont exact welke Homey payload zou worden gebouwd. Geen Run-knop. Geen live call.
        </div>
        <div className="grid sm:grid-cols-4 gap-2 text-xs">
          <select value={inspector.device_key}
            onChange={(e) => setInspector({ ...inspector, device_key: e.target.value })}
            className="border border-border rounded px-2 py-1"
            data-testid="inspector-device">
            {devices.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
          </select>
          <input value={inspector.capability_id}
            onChange={(e) => setInspector({ ...inspector, capability_id: e.target.value })}
            className="border border-border rounded px-2 py-1 font-mono"
            data-testid="inspector-capability" />
          <input value={inspector.value}
            onChange={(e) => setInspector({ ...inspector, value: e.target.value })}
            className="border border-border rounded px-2 py-1 font-mono"
            data-testid="inspector-value" />
          <button onClick={runInspector} className="px-3 py-1 bg-primary text-white rounded"
                  data-testid="inspector-run">Inspect</button>
        </div>
        {inspectResult && (
          <div className="mt-3 space-y-2 text-xs" data-testid="inspector-result">
            <div className="flex items-center gap-2">
              <span className="font-semibold">Validation:</span>
              {inspectResult.validation.validated
                ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                : <XCircle className="w-4 h-4 text-red-600" />}
              <span>{inspectResult.validation.result}</span>
              {inspectResult.validation.error && (
                <span className="text-red-700 font-mono">{inspectResult.validation.error}</span>
              )}
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-2 font-mono overflow-auto">
              <div className="font-bold mb-1">Homey SDK call</div>
              <pre>{JSON.stringify(inspectResult.homey_payload.sdk_call, null, 2)}</pre>
              <div className="font-bold mt-2 mb-1">HTTP request</div>
              <pre>{JSON.stringify(inspectResult.homey_payload.http_request, null, 2)}</pre>
            </div>
            <div className="space-y-1">
              <div className="font-semibold">Gate trace</div>
              {(inspectResult.gates || []).map((g, i) => (
                <div key={i} className="flex items-center gap-2">
                  {g.passed
                    ? <CheckCircle2 className="w-3 h-3 text-green-600" />
                    : <XCircle className="w-3 h-3 text-red-600" />}
                  <span className="font-mono">{g.gate}</span>
                  <span className="text-muted-foreground">{typeof g.info === 'object' ? JSON.stringify(g.info) : String(g.info)}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">Copy as cURL:</span>
              <button onClick={() => copy(inspectResult.copy_as_curl)}
                      className="px-2 py-1 bg-slate-200 rounded flex items-center gap-1"
                      data-testid="inspector-copy-curl">
                <Copy className="w-3 h-3" /> copy
              </button>
            </div>
            <div className="text-[11px] text-red-700">
              live_call_made: {String(inspectResult.control_summary.live_call_made)} ·
              live_endpoint: {inspectResult.control_summary.live_endpoint}
            </div>
          </div>
        )}
      </div>

      {/* Consent + Whitelist per device */}
      <div className="border border-border rounded-xl p-4 bg-white" data-testid="consent-whitelist-block">
        <div className="font-semibold mb-2">Consent & Whitelist</div>
        <div className="text-xs text-muted-foreground mb-3">
          Consent = klant geeft akkoord. Whitelist = pilot-set. Beide vereist voordat ook maar één live-test mag.
          Blocked devices (lock/alarm/unknown) kunnen niet gewhitelist worden.
        </div>
        {loading && <div className="text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Laden...</div>}
        <div className="grid gap-1 max-h-96 overflow-auto">
          <div className="grid grid-cols-12 gap-2 text-[11px] font-semibold border-b border-border py-1">
            <div className="col-span-5">Device</div>
            <div className="col-span-3">Category</div>
            <div className="col-span-2 text-center">Consent</div>
            <div className="col-span-2 text-center">Whitelist</div>
          </div>
          {devices.map((d) => (
            <div key={d.key} className="grid grid-cols-12 gap-2 text-xs py-1 items-center hover:bg-slate-50"
                 data-testid={`row-${d.key}`}>
              <div className="col-span-5 truncate">
                {d.name}
                {d.installer_required && <span className="ml-1 text-[10px] px-1 py-0.5 bg-amber-100 text-amber-800 rounded">installer</span>}
              </div>
              <div className="col-span-3 font-mono text-[10px]">{d.powersure_category}</div>
              <div className="col-span-2 text-center">
                <input type="checkbox" checked={consent[d.key] || false}
                       onChange={(e) => setConsentFor(d.key, e.target.checked)}
                       data-testid={`consent-${d.key}`} />
              </div>
              <div className="col-span-2 text-center">
                {blockedKeys.has(d.key) ? (
                  <span className="text-[10px] text-red-600 font-semibold">BLOCKED</span>
                ) : (
                  <input type="checkbox" checked={whitelist[d.key] || false}
                         onChange={(e) => setWhitelistFor(d.key, e.target.checked)}
                         data-testid={`whitelist-${d.key}`} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Audit log */}
      <div className="border border-border rounded-xl p-4 bg-white" data-testid="audit-block">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold">Audit log (laatste 50)</div>
          <div className="flex gap-2">
            <button onClick={load} className="text-xs px-2 py-1 border border-border rounded">Reload</button>
            <button onClick={exportAudit} className="text-xs px-2 py-1 border border-border rounded" data-testid="audit-export">Export JSON</button>
          </div>
        </div>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-[11px]" data-testid="audit-table">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left border-b border-border">
                <th className="py-1">ts</th><th>mode</th><th>device</th><th>cap</th>
                <th>value</th><th>result</th><th>live</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} className="border-b border-border/40"
                    data-testid={`audit-row-${a.id}`}>
                  <td className="py-1 font-mono">{new Date((a.ts || 0) * 1000).toLocaleTimeString()}</td>
                  <td className="font-mono">{a.mode}</td>
                  <td className="truncate max-w-[140px]">{a.device_key}</td>
                  <td className="font-mono">{a.capability_id}</td>
                  <td className="font-mono">{String(a.requested_value).slice(0, 12)}</td>
                  <td className={`font-mono ${a.result === 'dry_run_ok' || a.result === 'fake_ok' ? 'text-green-700' : 'text-red-700'}`}>
                    {a.result}
                  </td>
                  <td className="text-red-700 font-bold">{a.live_call_made ? '!!!' : 'no'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
        <div>
          <strong>Live control blijft 100% uit.</strong> Geen <code>setCapabilityValue</code>, geen Flows,
          geen write scopes, geen PAT. Adapter heeft geen HTTP-client by design.
          Feature flag <code>POWERSURE_HOMEY_LIVE_CONTROL_ENABLED</code> = OFF.
        </div>
      </div>
    </div>
  );
}
